/* eslint-disable */
import { Pose } from "@mediapipe/pose"
import { wait } from "./utils"
import FPSLimiter from './FPSLimiter';

const DEFAULT_POSE_OPTIONS = {
  modelComplexity: 1,
  smoothLandmarks: true,
  enableSegmentation: true,
  smoothSegmentation: true,
  minDetectionConfidence: 0.8,
  minTrackingConfidence: 0.5,
  useCpuInference: false,
}

// preload 與 init 要給同一組，不然 preload 會抓錯模型
export const MOBILE_SCAN_OPTIONS = { fps: 10, modelComplexity: 0 }

// rAF 的 callback 在 paint 之前跑，裡面再排一個 timeout 才是 paint 之後
const afterPaint = () => new Promise(resolve => {
  requestAnimationFrame(() => setTimeout(resolve, 0))
})

const BLANK_IMAGE = new Image()
BLANK_IMAGE.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIwAAAABJRU5ErkJggg=='

const PoseDetector = {
  _pause: true,
  _pose: null,
  _poseResults: {},
  _hasNewResults: false,
  _videoElement: null,
  _fpsLimter: null,
  _initPromise: null,

  // setOptions 必須在 initialize 之前 —— 模型檔是 initialize 時照 modelComplexity 抓的
  async preload(options = {}) {
    if (this._pose != null) { return }
    const now = Date.now()

    this._fpsLimter = Number.isFinite(options.fps) ? new FPSLimiter(options.fps) : null
    this._pose = new Pose({
      locateFile: file => `/mediapipe/${file}`,
    })
    this._pose.setOptions({ ...DEFAULT_POSE_OPTIONS, ...options })
    this._pose.onResults((results) => {
      this._poseResults = results
      this._hasNewResults = true
    })

    try {
      await this._pose.initialize()
    } catch (err) {
      // 壞掉的實例不能留著，否則之後的 preload / init 都會直接跳過它
      this._pose = null
      throw err
    }
    console.log('pose preloaded', (Date.now() - now) / 1000)
  },

  // 建圖與第一次推論很貴（實測手機上要十幾秒），所以只做一次，並且同時呼叫要共用
  // 同一個 promise —— 不然第二個呼叫會再送一張 blank image，又付一次。
  init(options = {}) {
    if (this._initPromise == null) {
      this._initPromise = this._kickOff(options).catch(err => {
        this._initPromise = null
        throw err
      })
    }
    return this._initPromise
  },

  async _kickOff(options) {
    const now = Date.now()
    await this.preload(options)
    await this.waitForBlankImage()
    // 上面兩個 await 在已經 preload 過時都是 microtask，整條鏈會接在呼叫端的
    // mounted 後面跑完，畫面就卡在切頁前不動。先讓瀏覽器畫完一格。
    await afterPaint()
    // send a blank image to kick-off the detector，繞過 FPSLimiter —— 那是給推論
    // 迴圈節流的，暖機被它擋掉的話就永遠不會暖了
    await this._pose.send({ image: BLANK_IMAGE })
    console.log('pose inited', (Date.now() - now) / 1000)
  },

  async waitForBlankImage() {
    if (!BLANK_IMAGE.complete) {
      await wait(0.1)
      await this.waitForBlankImage()
    }
  },

  async send(data) {
    if (this._fpsLimter && !this._fpsLimter.update()) { return }
    await this._pose.send(data)
  },

  start(videoElement) {
    this._videoElement = videoElement
    this.resume()
  },

  pause() {
    this._pause = true
  },

  resume() {
    this._pause = false
    this._onPoseUpdated()
  },

  stop() {
    this._pause = true
    this._videoElement = null
  },

  reset() {
    this._poseResults = {}
    this._hasNewResults = false
    this._pose.reset()
  },

  close() {
    this.stop();
    this._initPromise = null
    if (this._pose != null) {
      this._pose.close()
      this._pose = null
    }
  },

  getResults() {
    return this._poseResults
  },

  // 讀取即清除，所以只能有一個消費端
  consumeNewResults() {
    const hasNewResults = this._hasNewResults
    this._hasNewResults = false
    return hasNewResults
  },

  async _onPoseUpdated() {
    if (this._pause) { return }
    if (this._videoElement == null) { return }
    try {
      await this.send({ image: this._videoElement })
    } catch (err) {
      console.error(err)
      if (err.name.includes('RuntimeError')) {
        this.reset()
      }
    }
    requestAnimationFrame(() => this._onPoseUpdated())
  },
}

export default PoseDetector
